import type React from "react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

type Language = "sv" | "en";
type Currency = "SEK" | "EUR" | "USD" | "GBP";
type Location = "sweden" | "international";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  location: Location;
  setLocation: (location: Location) => void;
  t: (key: string, params?: Record<string, string>) => string;
}

// Currency types moved to currencyUtils.ts

const translations: Record<string, Record<Language, string>> = {
  // Header
  "nav.calculator": {
    sv: "Intäktsberäknare",
    en: "Revenue Calculator",
  },
  "nav.services": {
    sv: "Tjänster",
    en: "Services",
  },
  "nav.about": {
    sv: "Om oss",
    en: "About Us",
  },
  "nav.contact": {
    sv: "Kontakt",
    en: "Contact",
  },
  "cta.calculate": {
    sv: "Beräkna intäkter",
    en: "Calculate Revenue",
  },

  // Calculator
  "calculator.title": {
    sv: "Beräkna din potentiella intäkt",
    en: "Calculate Your Potential Revenue",
  },
  "calculator.description": {
    sv: "Använd vår kalkylator för att uppskatta möjliga intäkter från våra tjänster baserat på din kundvolym och konverteringsgrad.",
    en: "Use our calculator to estimate potential revenue from our services based on your customer volume and conversion rate.",
  },
  "calculator.advanced.title": {
    sv: "Avancerad ROI-kalkylator",
    en: "Advanced ROI Calculator",
  },
  "calculator.advanced.description": {
    sv: "Beräkna din potentiella avkastning på investering med våra tjänster.",
    en: "Calculate your potential return on investment with our services.",
  },
  "calculator.settings": {
    sv: "Kalkylatorinställningar",
    en: "Calculator Settings",
  },
  "calculator.changes.instant": {
    sv: "Ändringar tillämpas direkt",
    en: "Changes apply instantly",
  },
  "calculator.parameters.basic": {
    sv: "Grundläggande parametrar",
    en: "Basic Parameters",
  },
  "calculator.required": {
    sv: "Obligatorisk",
    en: "Required",
  },
  "calculator.time.period": {
    sv: "Tidsperiod",
    en: "Time Period",
  },
  "calculator.months.label": {
    sv: "månader",
    en: "months",
  },
  "calculator.months": {
    sv: "månader",
    en: "months",
  },
  "calculator.per.customer": {
    sv: "per kund",
    en: "per customer",
  },
  "calculator.customer.lifetime.value": {
    sv: "Kundlivstidsvärde",
    en: "Customer Lifetime Value",
  },
  "calculator.needs.improvement": {
    sv: "Behöver förbättras",
    en: "Needs Improvement",
  },
  "calculator.ltv.cac.ratio.explanation": {
    sv: "LTV:CAC-förhållandet visar hur mycket värde en kund genererar jämfört med kostnaden för att förvärva dem.",
    en: "The LTV:CAC ratio shows how much value a customer generates compared to the cost of acquiring them.",
  },
  "calculator.months.short": {
    sv: "mån",
    en: "mo",
  },
  "calculator.min": {
    sv: "Min",
    en: "Min",
  },
  "calculator.recommended": {
    sv: "Rekommenderad",
    en: "Recommended",
  },
  "calculator.tooltip.months": {
    sv: "Antal månader att beräkna resultat för.",
    en: "The number of months to calculate results for.",
  },
  "calculator.customers": {
    sv: "Kunder",
    en: "Customers",
  },
  "calculator.count": {
    sv: "antal",
    en: "count",
  },
  "calculator.tooltip.customers": {
    sv: "Antal kunder att inkludera.",
    en: "Number of customers to include.",
  },
  "calculator.additional.revenue": {
    sv: "Ytterligare intäkter",
    en: "Additional Revenue",
  },
  "calculator.per.customer.label": {
    sv: "Per kund",
    en: "Per customer",
  },
  "calculator.tooltip.additional.revenue": {
    sv: "Ytterligare månatliga intäkter.",
    en: "Additional monthly revenue.",
  },
  "calculator.financing.period": {
    sv: "Finansieringsperiod",
    en: "Financing Period",
  },
  "calculator.default": {
    sv: "Standard",
    en: "Default",
  },
  "calculator.amount": {
    sv: "Belopp",
    en: "Amount",
  },
  "calculator.product.distribution": {
    sv: "Produktfördelning",
    en: "Product Distribution",
  },
  "calculator.optional": {
    sv: "Valfri",
    en: "Optional",
  },
  "calculator.preset.scenarios": {
    sv: "Förinställda scenarier",
    en: "Preset Scenarios",
  },
  "calculator.scenario.conservative": {
    sv: "Konservativ",
    en: "Conservative",
  },
  "calculator.scenario.balanced": {
    sv: "Balanserad",
    en: "Balanced",
  },
  "calculator.scenario.aggressive": {
    sv: "Aggressiv",
    en: "Aggressive",
  },
  "calculator.reset": {
    sv: "Återställ",
    en: "Reset",
  },
  "calculator.default.values": {
    sv: "Standardvärden",
    en: "Default values",
  },
  "calculator.need.help": {
    sv: "Behöver du hjälp?",
    en: "Need Help?",
  },
  "calculator.view.methodology": {
    sv: "Visa metodik",
    en: "View methodology",
  },
  "calculator.roi": {
    sv: "ROI",
    en: "ROI",
  },
  "calculator.over": {
    sv: "Över",
    en: "Over",
  },
  "calculator.break.even": {
    sv: "BREAK-EVEN",
    en: "BREAK-EVEN",
  },
  "calculator.never": {
    sv: "Aldrig",
    en: "Never",
  },
  "calculator.time.to.recover": {
    sv: "Tid att återhämta kostnader",
    en: "Time to recover costs",
  },
  "calculator.net.cash": {
    sv: "NETTOKASSA",
    en: "NET CASH",
  },
  "calculator.total.profit.period": {
    sv: "Total vinst över perioden",
    en: "Total profit over period",
  },
  "calculator.net.cash.explanation": {
    sv: "Nettokassa representerar den totala kassaflödesvinsten över den angivna perioden, vilket ger en tydlig bild av den ekonomiska prestandan.",
    en: "Net cash represents the total cash flow profit over the specified period, providing a clear picture of financial performance.",
  },
  "calculator.roi.explanation": {
    sv: "Avkastning på investering (ROI) mäter lönsamheten för investeringen genom att jämföra vinsten med den initiala kostnaden.",
    en: "Return on Investment (ROI) measures the profitability of the investment by comparing the gain against the initial cost.",
  },
  "calculator.break.even.explanation": {
    sv: "Break-even-punkten visar hur många månader det tar innan de ackumulerade intäkterna täcker de totala kostnaderna.",
    en: "The break-even point shows how many months it takes for the accumulated revenues to cover the total costs.",
  },
  "calculator.good": {
    sv: "Bra",
    en: "Good",
  },
  "calculator.improve": {
    sv: "Förbättra",
    en: "Improve",
  },
  "calculator.current.value": {
    sv: "Aktuellt värde",
    en: "Current value",
  },
  "calculator.product.select": {
    sv: "Välj produkt",
    en: "Select Product",
  },
  "calculator.customers.title": {
    sv: "Hur många kunder vill du räkna på?",
    en: "How many customers do you want to calculate?",
  },
  "calculator.customers.label": {
    sv: "Antal kunder",
    en: "Number of customers",
  },
  "calculator.conversion.title": {
    sv: "Fyll i er estimerade konverteringsgrad på respektive tjänst",
    en: "Enter your estimated conversion rate for each service",
  },
  "calculator.conversion.rate": {
    sv: "Estimerad konverteringsgrad",
    en: "Estimated conversion rate",
  },
  "calculator.results.title": {
    sv: "Första årets beräknade omsättning (årsbasis)",
    en: "First year estimated revenue (annual basis)",
  },
  "calculator.currency.label": {
    sv: "Valuta",
    en: "Currency",
  },
  "calculator.revenue.annual": {
    sv: "Beräknad årsomsättning",
    en: "Estimated annual revenue",
  },
  "calculator.revenue.per.year": {
    sv: "per år",
    en: "per year",
  },
  "calculator.annual": {
    sv: "årsbasis",
    en: "annual basis",
  },
  "calculator.gross.revenue": {
    sv: "Bruttointäkt",
    en: "Gross Revenue",
  },
  "calculator.net.margin": {
    sv: "Nettomarginal",
    en: "Net Margin",
  },
  "calculator.disclaimer": {
    sv: "Vid beräkningen har vi använt antaganden kring genomsnittliga försäljningspriser och en uppskattning av adoptionstakten, både för befintliga eller potentiella kunder. Alla värden presenteras på årsbasis. För en mer detaljerad analys kan vi även granska marginaler och MRR på längre sikt, vilket ger en djupare förståelse för den potentiella tillväxten. Observera att faktiska resultat kan variera beroende på marknadsförhållanden, kundbeteende och andra externa faktorer, så dessa beräkningar bör ses som vägledande snarare än en garanti för framtida intäkter.",
    en: "In the calculation, we have used assumptions about average sales prices and an estimate of the adoption rate, both for existing or potential customers. All values are presented on an annual basis. For a more detailed analysis, we can also review margins and MRR in the longer term, which provides a deeper understanding of the potential growth. Please note that actual results may vary depending on market conditions, customer behavior, and other external factors, so these calculations should be seen as guidance rather than a guarantee of future revenue.",
  },
  "calculator.button": {
    sv: "Klicka här för att få tillgång till våra exakta uträkningar",
    en: "Click here to access our exact calculations",
  },
  "calculator.dialog.title": {
    sv: "Få tillgång till exakta uträkningar",
    en: "Get access to exact calculations",
  },
  "calculator.dialog.description": {
    sv: "Fyll i din e-postadress för att få tillgång till våra detaljerade uträkningar.",
    en: "Enter your email address to access our detailed calculations.",
  },
  "calculator.currency": {
    sv: "Välj valuta",
    en: "Select Currency",
  },
  // Form
  "form.name": {
    sv: "Namn",
    en: "Name",
  },
  "form.name.placeholder": {
    sv: "Ditt namn",
    en: "Your name",
  },
  "form.email": {
    sv: "E-post",
    en: "Email",
  },
  "form.email.placeholder": {
    sv: "din.email@företag.se",
    en: "your.email@company.com",
  },
  "form.submit": {
    sv: "Skicka",
    en: "Submit",
  },
  "form.sending": {
    sv: "Skickar...",
    en: "Sending...",
  },
  "form.success": {
    sv: "Tack! Din förfrågan har skickats.",
    en: "Thank you! Your request has been sent.",
  },
  "form.error": {
    sv: "Något gick fel. Försök igen senare.",
    en: "Something went wrong. Please try again later.",
  },

  // Services
  "service.fraud": {
    sv: "Fraud Protection",
    en: "Fraud Protection",
  },
  "service.smoke": {
    sv: "Smart Smoke",
    en: "Smart Smoke",
  },
  "service.monitoring": {
    sv: "Company Monitoring",
    en: "Company Monitoring",
  },

  // Footer
  "footer.description": {
    sv: "Vi erbjuder innovativa lösningar för att skydda ditt företag och maximera din tillväxt.",
    en: "We offer innovative solutions to protect your business and maximize your growth.",
  },
  "footer.services": {
    sv: "Tjänster",
    en: "Services",
  },
  "footer.company": {
    sv: "Företag",
    en: "Company",
  },
  "footer.about": {
    sv: "Om oss",
    en: "About Us",
  },
  "footer.careers": {
    sv: "Karriär",
    en: "Careers",
  },
  "footer.privacy": {
    sv: "Integritetspolicy",
    en: "Privacy Policy",
  },
  "footer.contact.description": {
    sv: "Har du frågor om våra tjänster eller vill diskutera en potentiell samarbetsmöjlighet?",
    en: "Do you have questions about our services or want to discuss a potential collaboration opportunity?",
  },
  "footer.contact.cta": {
    sv: "Kontakta oss",
    en: "Contact Us",
  },
  "footer.rights": {
    sv: "Alla rättigheter förbehållna.",
    en: "All rights reserved.",
  },

  // Benefits Section
  "benefits.title": {
    sv: "Fördelar för er och era kunder",
    en: "Benefits for you and your customers",
  },
  "benefits.subtitle": {
    sv: "Genom att erbjuda våra tjänster får både ditt företag och dina kunder värdefulla fördelar.",
    en: "By offering our services, both your company and your customers gain valuable benefits.",
  },
  "benefits.revenue.title": {
    sv: "Nya intäktsströmmar",
    en: "New revenue streams",
  },
  "benefits.revenue.description": {
    sv: "Öka dina återkommande intäkter genom att erbjuda våra värdefulla tilläggstjänster.",
    en: "Increase your recurring revenue by offering our valuable add-on services.",
  },
  "benefits.relationships.title": {
    sv: "Starkare kundrelationer",
    en: "Stronger customer relationships",
  },
  "benefits.relationships.description": {
    sv: "Bygg djupare kundrelationer genom att erbjuda tjänster som löser verkliga problem.",
    en: "Build deeper customer relationships by offering services that solve real problems.",
  },
  "benefits.competitive.title": {
    sv: "Konkurrensfördelar",
    en: "Competitive advantages",
  },
  "benefits.competitive.description": {
    sv: "Differentiera ditt erbjudande och sticka ut på marknaden med innovativa lösningar.",
    en: "Differentiate your offering and stand out in the market with innovative solutions.",
  },
  "benefits.scalable.title": {
    sv: "Skalbar inkomst",
    en: "Scalable income",
  },
  "benefits.scalable.description": {
    sv: "Enkelt att skala genom att addera till befintliga kundrelationer utan extra kostnader.",
    en: "Easy to scale by adding to existing customer relationships without extra costs.",
  },
  "benefits.security.title": {
    sv: "Kundsäkerhet",
    en: "Customer security",
  },
  "benefits.security.description": {
    sv: "Erbjud värdefulla skyddstjänster som ger dina kunder sinnesro och säkerhet.",
    en: "Offer valuable protection services that give your customers peace of mind and security.",
  },
  "benefits.analytics.title": {
    sv: "Analytisk insikt",
    en: "Analytical insight",
  },
  "benefits.analytics.description": {
    sv: "Få insikter i kundbeteende och identifiera ytterligare affärsmöjligheter.",
    en: "Gain insights into customer behavior and identify additional business opportunities.",
  },

  // Hero Section
  "hero.title": {
    sv: "Maximera dina intäkter med våra tjänster",
    en: "Maximize your revenue with our services",
  },
  "hero.subtitle": {
    sv: "Beräkna din potentiella inkomst med våra innovativa lösningar – stärk kundrelationer, öka intäkterna och få en konkurrensfördel.",
    en: "Calculate your potential income with our innovative solutions – strengthen customer relationships, increase revenue, and gain a competitive edge.",
  },
  "hero.cta.calculator": {
    sv: "Beräkna dina intäkter",
    en: "Calculate your revenue",
  },
  "hero.cta.contact": {
    sv: "Kontakta oss via formuläret",
    en: "Contact us via the form",
  },

  // CTA Section
  "cta.title": {
    sv: "Redo att öka din omsättning?",
    en: "Ready to increase your revenue?",
  },
  "cta.description": {
    sv: "Ta nästa steg och bli en partner för att börja erbjuda våra innovativa tjänster till dina kunder och öka dina intäkter.",
    en: "Take the next step and become a partner to start offering our innovative services to your customers and increase your revenue.",
  },
  "cta.access.calculations": {
    sv: "Klicka här för att få tillgång till våra exakta uträkningar",
    en: "Click here to access our exact calculations",
  },

  // Services Card
  "services.title": {
    sv: "Våra tjänster",
    en: "Our services",
  },
  "services.satisfaction": {
    sv: "Maximera dina återkommande intäkter",
    en: "Maximize your recurring revenue.",
  },
  "services.fraud.desc": {
    sv: "Skydda dina kunder mot bedrägeri och identitetsstöld.",
    en: "Protect your customers against fraud and identity theft.",
  },
  "services.fraud.desc.extended": {
    sv: "Vår Fraud Protection-tjänst erbjuder ett omfattande skydd mot bedrägerier och identitetsstöld för dina kunder. Genom avancerad säkerhetsteknik och realtidsövervakning kan vi identifiera misstänkta aktiviteter innan de leder till ekonomiska förluster.",
    en: "Our Fraud Protection service offers comprehensive protection against fraud and identity theft for your customers. Through advanced security technology and real-time monitoring, we can identify suspicious activities before they lead to financial losses.",
  },
  "services.smoke.desc": {
    sv: "Intelligent brandvarning och tidig insats.",
    en: "Intelligent fire alarm and early intervention.",
  },
  "services.smoke.desc.extended": {
    sv: "Smart Smoke är en smart brandvarningstjänst som använder IoT-teknik för att upptäcka både rök och kolmonoxid, vilket ger ett mer heltäckande brandskydd.",
    en: "Smart Smoke is an advanced fire alarm service that uses IoT technology to detect both smoke and carbon monoxide, providing more comprehensive fire protection.",
  },
  "services.monitoring.desc": {
    sv: "Kontinuerlig övervakning av kundernas företag.",
    en: "Continuous monitoring of customers' companies.",
  },
  "services.monitoring.desc.extended": {
    sv: "Company Monitoring erbjuder en heltäckande övervakningstjänst för företag som vill hålla sig uppdaterade om viktiga förändringar i sin affärsmiljö. Tjänsten övervakar kontinuerligt kreditvärdighet, ekonomiska nyckeltal, ägarförändringar och andra kritiska faktorer som påverkar affärsrelationer.",
    en: "Company Monitoring offers a comprehensive monitoring service for businesses that want to stay updated on important changes in their business environment. The service continuously monitors creditworthiness, financial key figures, ownership changes, and other critical factors that affect business relationships.",
  },
  "services.per.customer": {
    sv: "per kund",
    en: "per customer",
  },
  "calculator.key.metrics": {
    sv: "Nyckeltal",
    en: "Key Metrics",
  },
  "calculator.excellent.roi": {
    sv: "Utmärkt ROI",
    en: "Excellent ROI",
  },
  "calculator.investment.performance": {
    sv: "Investeringsprestanda",
    en: "Investment Performance",
  },
  "calculator.ltv.cac.ratio": {
    sv: "LTV:CAC-förhållande",
    en: "LTV:CAC Ratio",
  },
  "calculator.target": {
    sv: "Mål",
    en: "Target",
  },
  "calculator.payback.period": {
    sv: "Återbetalningstid",
    en: "Payback Period",
  },
  "calculator.fast": {
    sv: "Snabb",
    en: "Fast",
  },
  "calculator.customer.economics": {
    sv: "Kundekonomi",
    en: "Customer Economics",
  },
  "calculator.monthly.revenue": {
    sv: "Månatlig intäkt",
    en: "Monthly Revenue",
  },
  "calculator.percent.from.average": {
    sv: "+{{percent}} från genomsnitt",
    en: "+{{percent}} from average",
  },

  // Sensitivity Analysis
  "calculator.sensitivity.title": {
    sv: "Känslighetsanalys",
    en: "Sensitivity Analysis",
  },
  "calculator.sensitivity.scenarios": {
    sv: "Vad-om-scenarier",
    en: "What-If Scenarios",
  },
  "calculator.sensitivity.metric": {
    sv: "Mått:",
    en: "Metric:",
  },
  "calculator.price.adjustment": {
    sv: "Prisjustering",
    en: "Price Adjustment",
  },
  "calculator.churn.adjustment": {
    sv: "Churnjustering",
    en: "Churn Adjustment",
  },
  "calculator.cac.adjustment": {
    sv: "CAC-justering",
    en: "CAC Adjustment",
  },
  "calculator.adjusted.price": {
    sv: "Justerat pris:",
    en: "Adjusted Price:",
  },
  "calculator.adjusted.churn": {
    sv: "Justerad churn:",
    en: "Adjusted Churn:",
  },
  "calculator.adjusted.cac": {
    sv: "Justerad CAC:",
    en: "Adjusted CAC:",
  },
  "calculator.per.month": {
    sv: "/ månad",
    en: "/ month",
  },
  "calculator.per.customer.short": {
    sv: "/ kund",
    en: "/ customer",
  },
  "calculator.roi.impact": {
    sv: "ROI-påverkan",
    en: "ROI Impact",
  },
  "calculator.ltvcac.impact": {
    sv: "LTV:CAC-påverkan",
    en: "LTV:CAC Ratio Impact",
  },
  "calculator.breakeven.impact": {
    sv: "Break-even-påverkan",
    en: "Break-even Impact",
  },
  "calculator.breakeven.point": {
    sv: "Break-even-punkt",
    en: "Break-even Point",
  },
  "calculator.sensitivity": {
    sv: "Känslighet",
    en: "Sensitivity",
  },
  "calculator.base": {
    sv: "Bas:",
    en: "Base:",
  },
  "calculator.no.change": {
    sv: "Ingen förändring",
    en: "No change",
  },
  "calculator.months.abbreviated": {
    sv: "månader",
    en: "months",
  },
  "calculator.optimization": {
    sv: "Optimeringsrekommendationer",
    en: "Optimization Recommendations",
  },
  "calculator.impactful.lever": {
    sv: "Mest effektiva hävstång:",
    en: "Most Impactful Lever:",
  },
  "calculator.price.impact": {
    sv: "Prisjusteringar har starkast påverkan på din lönsamhet.",
    en: "Price adjustments have the strongest impact on your profitability.",
  },
  "calculator.churn.impact": {
    sv: "Att minska churn kommer att ha den mest betydande långsiktiga påverkan på din verksamhet.",
    en: "Reducing churn will have the most significant long-term impact on your business.",
  },
  "calculator.optimal.price": {
    sv: "Optimal prispunkt:",
    en: "Optimal Price Point:",
  },
  "calculator.price.elasticity": {
    sv: "Testa priselasticitet för att hitta optimal balans mellan konvertering och intäkter.",
    en: "Test price elasticity to find the optimal balance between conversion and revenue.",
  },
  "calculator.price.increase.roi": {
    sv: "Att öka priserna med {{percent}}% kan förbättra ROI till {{roi}}.",
    en: "Increasing prices by {{percent}}% could improve ROI to {{roi}}.",
  },
  "calculator.cac.efficiency": {
    sv: "CAC-effektivitet:",
    en: "CAC Efficiency:",
  },
  "calculator.cac.change.impact": {
    sv: "En {{direction}} på {{percent}}% i anskaffningskostnader skulle {{impact}} break-even-tiden med {{months}} månader.",
    en: "A {{direction}} of {{percent}}% in acquisition costs would {{impact}} break-even time by {{months}} months.",
  },
  "calculator.decrease": {
    sv: "minskning",
    en: "decrease",
  },
  "calculator.increase": {
    sv: "ökning",
    en: "increase",
  },
  "calculator.reduce": {
    sv: "minska",
    en: "reduce",
  },
  "calculator.increase.verb": {
    sv: "öka",
    en: "increase",
  },

  // Currency
  "currency.title": {
    sv: "Valuta",
    en: "Currency",
  },
  "currency.sek": {
    sv: "Svenska kronor (SEK)",
    en: "Swedish Krona (SEK)",
  },
  "currency.eur": {
    sv: "Euro (EUR)",
    en: "Euro (EUR)",
  },
  "currency.usd": {
    sv: "US Dollar (USD)",
    en: "US Dollar (USD)",
  },
  "currency.gbp": {
    sv: "Brittiska pund (GBP)",
    en: "British Pound (GBP)",
  },

  // Location
  "location.title": {
    sv: "Plats",
    en: "Location",
  },
  "location.sweden": {
    sv: "Sverige",
    en: "Sweden",
  },
  "location.international": {
    sv: "International",
    en: "International",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>("sv");
  const [currency, setCurrency] = useState<Currency>("SEK");
  const [location, setLocation] = useState<Location>("sweden");

  // Function to update URL parameters - only include what's needed
  const updateUrlParams = useCallback(
    (lang: Language) => {
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        const isAdvancedPage = window.location.pathname === "/advanced";

        // Remove all language and location parameters
        url.searchParams.delete("l");
        url.searchParams.delete("lang");
        url.searchParams.delete("loc");
        url.searchParams.delete("location");

        // Always add language parameter
        url.searchParams.set("l", lang);

        // Only add location parameter on advanced page
        if (isAdvancedPage) {
          url.searchParams.set("loc", location);
        }

        // Update URL without reloading the page
        window.history.pushState({}, "", url.toString());
      }
    },
    [location]
  );

  // Check URL for language and location parameters on initial load
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const isAdvancedPage = window.location.pathname === "/advanced";

      // Support both short and long parameter names, prioritize short ones
      const langParam = urlParams.get("l") || (urlParams.get("lang") as Language);
      const locationParam = urlParams.get("loc") || (urlParams.get("location") as Location);

      let newLang = language;
      let shouldUpdateUrl = false;

      if (langParam === "en" || langParam === "sv") {
        newLang = langParam;
        setLanguage(langParam);
        shouldUpdateUrl = true;
      }

      // Only process location parameters on the advanced page
      if (isAdvancedPage && (locationParam === "sweden" || locationParam === "international")) {
        setLocation(locationParam);

        // If location is international, set currency to EUR
        if (locationParam === "international") {
          setCurrency("EUR");
        } else if (locationParam === "sweden") {
          setCurrency("SEK");
        }
      }

      // Clean up URL parameters
      if (shouldUpdateUrl || urlParams.has("lang") || urlParams.has("location")) {
        updateUrlParams(newLang);
      }
    }
  }, [language, updateUrlParams]);

  // Update language and URL
  const handleLanguageChange = useCallback(
    (lang: Language) => {
      setLanguage(lang);

      if (typeof window !== "undefined") {
        // Use the updateUrlParams function to ensure consistent URL parameters
        updateUrlParams(lang);
      }
    },
    [updateUrlParams]
  );

  // Update location and URL - only on advanced page
  const handleLocationChange = useCallback(
    (loc: Location) => {
      setLocation(loc);

      // If changing to international, switch to English if currently in Swedish
      if (loc === "international" && language === "sv") {
        setLanguage("en");
      }

      if (typeof window !== "undefined" && window.location.pathname === "/advanced") {
        // Only update location parameter on advanced page
        // If we're switching to international and currently in Swedish, updateUrlParams will use 'en'
        updateUrlParams(loc === "international" && language === "sv" ? "en" : language);
      }
    },
    [language, updateUrlParams]
  );

  const t = useCallback(
    (key: string, params?: Record<string, string>): string => {
      if (!translations[key]) {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }

      let text = translations[key][language];

      // Replace parameters if provided
      if (params) {
        Object.entries(params).forEach(([param, value]) => {
          text = text.replace(`{{${param}}}`, value);
        });
      }

      return text;
    },
    [language]
  );

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: handleLanguageChange,
        currency,
        setCurrency,
        location,
        setLocation: handleLocationChange,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
